import { useCallback, useEffect, useState } from 'react'

import { useSocket } from '@/providers/SocketProvider'
import { useMutation } from '@api/hooks'
import NotificationCard from '@components/NotificationCard/NotificationCard'
import { Notification } from '@domain'
import { Box } from '@mui/material'
import NotificationsApi, { NotificationsKeys } from '@services/notifications.api'
import { useQueryClient } from '@tanstack/react-query'

export type NotificationToast = {
	notification: Notification
	exiting?: boolean
}

/** Duration of the slide-out transition; also the delay before an exiting toast is removed. */
const SLIDE_MS = 250

// Limit number of toasts rendered at once to avoid overwhelming the UI
const MAX_PEEKS = 2

const NOTIFICATION_RECEIVED = 'notificationReceived'

/**
 * Bottom-left stack of webhook-driven notification toasts. The queue lives here —
 * the socket pushes in, cards dismiss out — and `NotificationToastsUI` renders it.
 * Toasts are keyed by `notification.id` (pushes dedupe on it) and dismissal is
 * two-stage: `exiting` plays the slide-out transition, then the toast is removed
 * after `SLIDE_MS`.
 */
export default function NotificationToasts() {
	const [toasts, setToasts] = useState<NotificationToast[]>([])

	const { lastJsonMessage } = useSocket()
	const queryClient = useQueryClient()

	const invalidateNotifications = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: NotificationsKeys.all })
	}, [queryClient])

	const pushToast = useCallback((notification: Notification) => {
		setToasts((current) =>
			current.some((t) => t.notification.id === notification.id)
				? current
				: [{ notification }, ...current]
		)
	}, [])

	const dismissToast = useCallback((id: Id) => {
		setToasts((current) => {
			if (!current.some((t) => t.notification.id === id && !t.exiting)) return current
			return current.map((t) => (t.notification.id === id ? { ...t, exiting: true } : t))
		})
		setTimeout(() => {
			setToasts((current) => current.filter((t) => t.notification.id !== id))
		}, SLIDE_MS)
	}, [])

	useEffect(() => {
		if (lastJsonMessage?.type !== NOTIFICATION_RECEIVED) return
		pushToast(lastJsonMessage.payload as Notification)
	}, [lastJsonMessage, pushToast])

	const markRead = useMutation({
		mutationFn: (id: Id) => NotificationsApi.markRead(id),
		onSuccess: invalidateNotifications,
	})

	const handleAction = (notification: Notification) => {
		markRead.mutate(notification.id)
		dismissToast(notification.id)
	}

	return (
		<NotificationToastsUI
			toasts={toasts.slice(0, MAX_PEEKS + 1)}
			onAction={handleAction}
			onClose={dismissToast}
		/>
	)
}

type UIProps = {
	toasts: NotificationToast[]
	onAction: (notification: Notification) => void
	onClose: (id: Id) => void
}

export function NotificationToastsUI({ toasts, onAction, onClose }: UIProps) {
	return (
		<Box
			sx={{
				position: 'fixed',
				bottom: toasts.length > 1 ? '4rem' : '1rem',
				left: '1rem',
				zIndex: (t) => t.zIndex.snackbar,
				transition: `bottom ${SLIDE_MS}ms ease`,
			}}
		>
			{toasts.map((t, i) => (
				<Box
					key={t.notification.id}
					role={i === 0 ? 'status' : undefined}
					aria-live={i === 0 ? 'polite' : undefined}
					sx={{
						'--ty': `calc(${i} * (100% + 8px))`,
						'@keyframes toast-slide-in': {
							from: { opacity: 0, transform: 'translateY(var(--ty)) translateY(-100%)' },
							to: { opacity: 1, transform: 'translateY(var(--ty))' },
						},
						animation: `toast-slide-in ${SLIDE_MS}ms ease`,
						position: 'absolute',
						bottom: 0,
						left: 0,
						width: 400,
						zIndex: toasts.length - i,
						pointerEvents: i === 0 && !t.exiting ? 'auto' : 'none',
						transition: `transform ${SLIDE_MS}ms ease, opacity ${SLIDE_MS}ms ease`,
						transform: t.exiting
							? 'translateY(var(--ty)) translateX(calc(-1 * (100% + 32px)))'
							: 'translateY(var(--ty))',
						opacity: t.exiting ? 0 : 1,
					}}
				>
					<NotificationCard
						variant="toast"
						notification={t.notification}
						onAction={onAction}
						onClose={() => onClose(t.notification.id)}
					/>
				</Box>
			))}
		</Box>
	)
}
